import { apiFetch } from "@/lib/api";
import {
  Bell, Calendar, DollarSign, AlertTriangle, Info,
  Video, MessageSquare, CreditCard, Star, Package,
  CheckCircle, XCircle, Clock,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

/* ── icon + colour per notification category ───────────────────────────── */
function getNotifMeta(type: string): { Icon: any; colour: string } {
  if (type.startsWith("BOOKING") || type === "DROPIN_BOOKING_RECEIVED") {
    return { Icon: Calendar, colour: "text-blue-400 bg-blue-500/15" };
  }
  if (type.startsWith("VIDEO")) {
    return { Icon: Video, colour: "text-purple-400 bg-purple-500/15" };
  }
  if (type === "SESSION_REMINDER") {
    return { Icon: Clock, colour: "text-amber-400 bg-amber-500/15" };
  }
  if (type.startsWith("PAYMENT") || type.startsWith("PACKAGE") || type.startsWith("SUBSCRIPTION")) {
    return { Icon: DollarSign, colour: "text-emerald-400 bg-emerald-500/15" };
  }
  if (type.startsWith("MEMBERSHIP")) {
    return { Icon: CreditCard, colour: "text-violet-400 bg-violet-500/15" };
  }
  if (type === "RATING_RECEIVED") {
    return { Icon: Star, colour: "text-yellow-400 bg-yellow-500/15" };
  }
  if (type === "CHAT_MESSAGE_NEW") {
    return { Icon: MessageSquare, colour: "text-sky-400 bg-sky-500/15" };
  }
  if (type === "CALENDAR_SYNC_FAILED" || type.includes("FAILED") || type.includes("CANCELLED")) {
    return { Icon: AlertTriangle, colour: "text-red-400 bg-red-500/15" };
  }
  return { Icon: Info, colour: "text-muted-foreground bg-white/5" };
}

export default function NotificationBell() {
  const [, navigate] = useLocation();
  const { data, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => apiFetch<{ notifications: any[]; unreadCount: number }>("/notifications/my"),
    refetchInterval: 30000,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const items = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function markRead(id: string) {
    try {
      await apiFetch(`/notifications/${id}/read`, { method: "PUT" });
      refetch();
    } catch {}
  }

  async function markAllRead() {
    try {
      await apiFetch("/notifications/read-all", { method: "PUT" });
      refetch();
    } catch {}
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-card border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="font-display font-bold text-white text-sm uppercase tracking-wide">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-[10px] font-mono bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 ? (
              <div className="p-10 text-center">
                <Bell className="w-9 h-9 text-muted-foreground mx-auto mb-3 opacity-25" />
                <p className="text-sm font-medium text-muted-foreground">All caught up!</p>
                <p className="text-xs text-muted-foreground/60 mt-1">No notifications yet</p>
              </div>
            ) : (
              items.slice(0, 20).map((n: any) => {
                const { Icon, colour } = getNotifMeta(n.type);
                return (
                  <div
                    key={n.id}
                    className={cn(
                      "flex items-start gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors cursor-pointer",
                      !n.isRead && "bg-primary/[0.04]"
                    )}
                    onClick={() => { if (!n.isRead) markRead(n.id); }}
                  >
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5", colour)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm leading-snug",
                        !n.isRead ? "text-white font-medium" : "text-muted-foreground"
                      )}>
                        {n.message}
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                    {!n.isRead && <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2 flex-none" />}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-white/10 px-4 py-2.5">
              <button
                onClick={() => { setOpen(false); navigate("/notifications"); }}
                className="text-xs text-primary hover:underline w-full text-center"
              >
                View all notifications →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
